"use client";

import * as React from "react";

export type ElementSize = { width: number; height: number };

const ZERO: ElementSize = { width: 0, height: 0 };

/**
 * Live border-box size of one element, measured with a `ResizeObserver`.
 *
 * Attach `ref` to the element you want to measure and read `size`. It updates
 * when the element mounts and on every resize afterwards, and it measures the
 * element you point it at — not the window — so a component that sizes itself
 * against its own column keeps working inside panes, drawers and split views.
 *
 * `offsetWidth`/`offsetHeight` (not `clientWidth`) so borders count: layout
 * math that adds up padding + children has to compare against the same box
 * the border is drawn on. Both are already integers.
 *
 * Zero means "not measured yet" as well as "genuinely empty" — the two are
 * indistinguishable from here, and callers that gate on a real measurement
 * should treat `0` as absent (`size.height || null`).
 *
 * ── `ref` is a CALLBACK ref, and that is load-bearing. ──
 *
 * The element frequently mounts LATER than the component holding this hook: a
 * hook called at the top of a dialog measures a node that does not exist until
 * the dialog opens, because an overlay renders no popup subtree while closed.
 * An object ref cannot see that — the mount effect reads `null`, returns, and
 * nothing re-runs when the node arrives, so the size stays `0` forever with no
 * error anywhere. React calls a callback ref when the node itself attaches and
 * detaches, which is exactly the event this needs.
 *
 * It carries `.current` as well, so a caller that reads the node directly
 * (measuring a sibling, reading `scrollHeight`) still can.
 */
export function useElementSize<T extends HTMLElement = HTMLElement>(): {
  ref: React.RefCallback<T> & React.RefObject<T | null>;
  size: ElementSize;
} {
  const [node, setNode] = React.useState<T | null>(null);
  const [size, setSize] = React.useState<ElementSize>(ZERO);

  // Stable identity: React re-invokes a callback ref whose identity changed, so
  // a fresh function each render would detach and re-attach on every render.
  const ref = React.useMemo(() => {
    const attach = ((el: T | null) => {
      attach.current = el;
      setNode(el);
    }) as React.RefCallback<T> & { current: T | null };
    attach.current = null;
    return attach;
  }, []);

  React.useLayoutEffect(() => {
    if (!node) {
      // The node detached (overlay closed, branch swapped). Drop back to "not
      // measured" rather than keeping the last size, which would otherwise be
      // served as fact against whatever mounts next.
      setSize(ZERO);
      return;
    }
    const update = () => {
      const width = node.offsetWidth;
      const height = node.offsetHeight;
      setSize((prev) =>
        prev.width === width && prev.height === height
          ? prev
          : { width, height },
      );
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);

  return { ref, size };
}
